import { z } from 'zod';
import { adPlatformSchema } from './ad-platform.js';
import { attributionPolicyProposalPayloadSchema } from './attribution-policy-card.js';
import { autopilotWeeklyRecapPayloadSchema } from './autopilot-recap.js';
import { MAX_DRAFT_CARD_PARTS } from './brand-brain-drafts.js';
import { brandEvolutionAutoAppliedPayloadSchema, brandEvolutionCardPayloadSchema, } from './brand-coach.js';
import { campaignRollupCardPayloadSchema } from './campaign-rollup-card.js';
import { campaignVerdictSchema } from './campaign-verdict.js';
import { creativeDropPayloadSchema } from './creative-drop.js';
import { crossChannelBudgetProposalPayloadSchema } from './cross-channel-budget.js';
import { fridayBriefPayloadSchema } from './friday-brief.js';
import { aiVisibilityEvidenceSchema, aiVisibilityOutputSchema } from './mag-ai-visibility.js';
import { magCampaignProposalCardPayloadSchema } from './mag-campaign-proposer.js';
import { magChannelProposalCardPayloadSchema } from './mag-channel-proposer.js';
import { funnelDiagnosisOutputSchema } from './mag-funnel-diagnosis.js';
import { seoAuditEvidenceSchema, seoAuditOutputSchema, seoLighthouseScoresSchema, } from './mag-seo-audit.js';
import { metaContentPlanCardPayloadSchema } from './meta-content-plan.js';
import { metaEngagementDigestCardPayloadSchema } from './meta-engagement.js';
import { metaHistoryLearnCardPayloadSchema } from './meta-history.js';
import { mondayRecapPayloadSchema } from './monday-recap.js';
import { monthlyPlanPayloadSchema } from './monthly-plan.js';
import { paidStrategyRefreshProposalPayloadSchema } from './paid-strategy-refresh.js';
import { trustMigrationProposalPayloadSchema } from './trust-ledger.js';
// Feed card payload schemas — spec §11. The `feed_cards` table has a single
// jsonb `payload` column; this file owns the per-kind shape so the api can
// validate before insert and the dashboard can render with confidence.
export const conceptFormatSchema = z.enum(['static_image', 'video', 'carousel']);
/** s262 — the SHAPE of a concept. Distinct from `format` (which is the
 *  media: static / video / carousel). Shape describes the rhetorical
 *  structure of the hook: a statement vs a question vs a stat etc.
 *
 *  Mag's concept-agent classifies each concept by shape AND uses prior
 *  concepts' shapes to vary across a drop — without this, the agent
 *  tends to repeat her most-confident shape (usually `statement`)
 *  across every concept in a slate, which then collapses to the same
 *  template (hook-card-v1) every time even though the brand has many
 *  templates available.
 *
 *  Closed taxonomy so the agent picks from a fixed vocabulary instead
 *  of inventing labels. Maps roughly 1:1 with the template-router's
 *  shape signals — `statement` → hook-card, `question` → big-question,
 *  `stat` → stat-bomb, etc. */
export const conceptShapeSchema = z.enum([
    'statement', // declarative hook ("you can't grow without the right deck")
    'question', // rhetorical / provocative ("¿cuántas horas perdiste en filas?")
    'stat', // number-led ("87% of founders skip this step")
    'quote', // customer / founder voice with attribution
    'comparison', // X vs Y / before vs after
    'list', // N items / N steps / N reasons
    'event', // date / launch / cohort start / sale ending
    'pov', // founder-voice talking-head ("i did X")
]);
/**
 * Concept proposal — Mag's first strategic move after reading the brief.
 * One ad concept: the hook the viewer reads, the strategic angle behind it,
 * the format she'd run it in, and a short rationale tying it back to the
 * brand brief. No production assets yet — that's the next session.
 */
export const conceptCardPayloadSchema = z.object({
    kind: z.literal('concept_proposal'),
    hook: z.string().min(1).max(140),
    angle: z.string().min(1).max(200),
    format: conceptFormatSchema,
    /** s262 — rhetorical shape of the hook. Optional + defaults to
     *  'statement' so older rows (pre-s262) deserialize cleanly. New
     *  concepts always carry the shape mag emitted. */
    shape: conceptShapeSchema.default('statement'),
    rationale: z.string().min(1).max(800),
});
/**
 * Welcome card — no structured payload today; the title + body on the
 * feed_cards row carry everything. The literal `kind` field keeps the
 * union discriminated so we can add structure later without a migration.
 */
export const welcomeCardPayloadSchema = z.object({
    kind: z.literal('welcome'),
});
/**
 * Creative variants — the production-side output of a concept proposal.
 * Five hook variations (the founder picks one or none) plus a single image
 * prompt that fits the concept. `parentConceptId` links the variants back
 * to the concept they came from so the dashboard can render the lineage.
 *
 * No actual image generation here yet — that's its own session.
 */
export const creativeVariantsCardPayloadSchema = z.object({
    kind: z.literal('creative_variants'),
    parentConceptId: z.string().uuid(),
    hooks: z.array(z.string().min(1).max(180)).min(3).max(5),
    imagePrompt: z.string().min(1).max(600),
    // Set when the founder triggers image generation. OpenAI hosts the URL
    // for ~1h — `imageExpiresAt` is the rough cutoff so the UI can show a
    // "regenerate" affordance after expiry without a HEAD request.
    imageUrl: z.string().url().optional(),
    imageExpiresAt: z.string().optional(),
    imageGeneratedAt: z.string().optional(),
});
/**
 * Analytics finding — Mag's interpretation of what she's seeing in the
 * mag.js event stream. Lands in /home like every other Mag card so
 * everything she says lives in one place. The dashboard's /analytics
 * page also reads these to render the "what mag is seeing" panel — same
 * card, filtered view.
 *
 * The shape is intentionally narrow: one observation per card. Multiple
 * findings = multiple cards. Mag should err on the side of saying less.
 */
export const analyticsFindingCardPayloadSchema = z.object({
    kind: z.literal('analytics_finding'),
    /** Which analytics_projects row this finding is about. Lets the
     *  dashboard scope per-project filters cleanly. */
    projectId: z.string().uuid(),
    /** The path mag's reasoning about, when it's path-specific.
     *  Site-wide findings ('your blog index pulls 80% of traffic but no
     *  conversions') leave this null. */
    path: z.string().min(1).max(2048).nullable(),
    /** The metric Mag noticed, in her voice + numerical receipt. Single
     *  line — e.g. "78% of visitors to /pricing bounce before the 50%
     *  scroll mark." Founders read this as the "what". */
    observedMetric: z.string().min(1).max(280),
    /** Specific action Mag would take. Short imperative. Lowercase. */
    recommendedAction: z.string().min(1).max(280),
    /** Blast-radius pill for the action — same scale as the takeover-read
     *  agent's actions so the dashboard renders them consistently. */
    blastRadius: z.enum(['low', 'medium', 'high']),
});
export const funnelDiagnosisCardPayloadSchema = z.object({
    kind: z.literal('funnel_diagnosis'),
    /** The conversion-funnel leak Mag found. */
    leakStep: z.string(),
    prevStep: z.string(),
    /** 0..1 — share of the prev step's visitors who dropped here. */
    dropOffPct: z.number().min(0).max(1),
    confidence: z.enum(['confident', 'tentative']),
    /** Visitors who reached the step before the drop (the denominator). */
    sampleSize: z.number().int().min(0),
    /** Mag's read — an evidence-cited diagnosis, or an honest "need more signal
     *  first" with what to instrument. */
    output: funnelDiagnosisOutputSchema,
    generatedAt: z.string().datetime(),
    costUsd: z.number().min(0),
});
export const seoAuditCardPayloadSchema = z.object({
    kind: z.literal('seo_audit'),
    /** The marketing-site URL Mag audited. */
    url: z.string().url(),
    /** Lighthouse/PageSpeed strategy. Mobile-first in v1. */
    strategy: z.literal('mobile'),
    /** Lighthouse category scores (0..1). Null fields = PageSpeed was
     *  unreachable; the audit then rests on the on-page evidence only. */
    scores: seoLighthouseScoresSchema,
    /** Mag's read — grouped findings (performance/on-page/messaging) or an
     *  honest "nothing material to fix right now". */
    output: seoAuditOutputSchema,
    /** The raw deterministic evidence the analysis rests on (failed Lighthouse
     *  audits, scraped copy, positioning). nullish for cards created before this
     *  shipped — those render the conclusions without the evidence section. */
    evidence: seoAuditEvidenceSchema.nullish(),
    generatedAt: z.string().datetime(),
    costUsd: z.number().min(0),
});
/** Spec a6132cb2 / s5 — is this site legible to the engines that answer buying
 *  questions?
 *
 *  Separate from seo_audit deliberately: `decision` is one field per output and
 *  'all_clear' gates card creation, so a site clean on classic SEO but
 *  unreachable by OAI-SearchBot has no representable state inside the
 *  seo_audit output. */
export const aiVisibilityCardPayloadSchema = z.object({
    kind: z.literal('ai_visibility'),
    /** The marketing-site URL that was checked. */
    url: z.string().url(),
    /** Mag's read — grouped findings, or an honest "you're reachable". */
    output: aiVisibilityOutputSchema,
    /** The deterministic evidence, kept INDEPENDENT of the agent prose above.
     *  The spec's flip condition is that if answer_shape adds little the agent
     *  gets dropped and this card renders from signals alone — which stays
     *  possible only while the payload is not agent-shaped. */
    evidence: aiVisibilityEvidenceSchema,
    generatedAt: z.string().datetime(),
    costUsd: z.number().min(0),
});
/** Spec 3c98725c — a launched campaign that CANNOT perform as built.
 *
 *  Deliberately distinct from the spend and performance cards: those
 *  report what a campaign DID, and a campaign that cannot serve did
 *  nothing, so it never trips them. This card is how a founder learns
 *  their money is parked rather than working — the failure that stayed
 *  invisible until one of them opened the Google Ads UI by chance. */
export const campaignHealthCardPayloadSchema = z.object({
    kind: z.literal('campaign_health'),
    /** Magig campaign id, so the card can link straight to the page
     *  carrying the panel and the fix. */
    campaignId: z.string().uuid(),
    campaignName: z.string().min(1).max(200),
    /** Mag's id for the google_search_ad deliverable the findings are
     *  about (e.g. "search-celular-voz"). */
    deliverableId: z.string().min(1).max(64),
    findings: z
        .array(z.object({
        kind: z.literal('underbid_keyword'),
        keyword: z.string().min(1).max(80),
        criterionId: z.string().min(1).max(120),
        currentBidUsd: z.number().min(0),
        firstPageCpcUsd: z.number().min(0),
        /** What the fix buys, not just what it costs. Null when the
         *  budget is unknown. A bid recommendation without this quietly
         *  trades clicks for position. */
        clicksPerDayAtTargetBid: z.number().min(0).nullable(),
        dailyBudgetUsd: z.number().min(0),
    }))
        .max(20),
    /** Keywords google has not served for weeks and has never priced
     *  (spec 97d4a11b). A DIFFERENT question from the bids above: those
     *  are about paying enough to win an auction, these are about there
     *  being no auction. The action is pause, never remove.
     *
     *  Optional because cards written before this existed carry none,
     *  and absent must not read as "none found".
     *
     *  `findings` lost its .min(1) so a card can carry only these. The
     *  card gate (isMaterial) still requires at least one of the two —
     *  an empty card is impossible by construction, not by schema. */
    deadKeywords: z
        .array(z.object({
        kind: z.literal('dead_keyword'),
        keyword: z.string().min(1).max(80),
        criterionId: z.string().min(1).max(120),
        rarelyServedSince: z.string().datetime(),
        daysRarelyServed: z.number().int().min(0),
    }))
        .max(20)
        .optional(),
    generatedAt: z.string().datetime(),
});
/** Spec f510708e s2 — a launched campaign whose FUNNEL says something
 *  worth acting on: it is being shown and nobody clicks, or it is
 *  starved and the lever is nameable.
 *
 *  DELIBERATELY NOT FOLDED INTO campaign_health. That card's gate fires
 *  only on an underbid keyword, and a campaign can have perfectly
 *  priced bids while losing 86% of its impressions to its own daily
 *  budget — which is exactly the campaign that prompted this. Sharing
 *  the gate would hide the funnel read on the campaigns that need it
 *  most, which is the failure campaign_health itself exists to prevent.
 *
 *  THE PAYLOAD IS A FROZEN POINTER, not the truth. It records what the
 *  funnel said when Mag spoke; the drawer re-reads live state, so a
 *  founder who raised the budget five minutes ago does not open the card
 *  and see the old number. Same rule the campaign_health drawer follows. */
export const campaignFunnelCardPayloadSchema = z.object({
    kind: z.literal('campaign_funnel'),
    campaignId: z.string().uuid(),
    campaignName: z.string().min(1).max(200),
    /** The deliverable the lever belongs to, when exactly one carries an
     *  impression share. Absent when several do — naming one lever for a
     *  multi-deliverable campaign would point the founder at the wrong ad. */
    deliverableId: z.string().min(1).max(64).optional(),
    /** Which reading fired: being shown and unloved, or starved. */
    reason: z.enum(['no_clicks', 'starved']),
    /** For `starved`, WHICH loss dominated — the daily budget, or ad rank.
     *
     *  Persisted so an open card's verdict can be compared against a fresh
     *  reading, which is the only way to notice that it INVERTED. Before this
     *  the cause lived solely in the rendered title, and the dedupe (one open
     *  card) plus a resolver that fires only when the campaign stops being
     *  starved meant a card saying "more budget won't help" survived the data
     *  reversing under it. Found live on fiscalo, stale three days.
     *
     *  OPTIONAL, and that is not laziness: rows written before this field
     *  existed carry no cause, and a required key would fail the payload parse
     *  on every one of them. A `starved` card with no cause is treated as
     *  stale once, which self-heals the existing rows on the next tick.
     *
     *  Absent by design on `no_clicks`, which names no loss to split. */
    cause: z.enum(['budget', 'rank']).optional(),
    impressions: z.number().int().min(0),
    clicks: z.number().int().min(0),
    /** Mag's sentence at the moment she spoke. The drawer prefers live
     *  state over this. */
    line: z.string().min(1).max(600),
    /** Window the reading is about, so the card cannot imply a 30-day
     *  story from a 7-day number. */
    windowDays: z.number().int().min(1).max(90),
    generatedAt: z.string().datetime(),
});
/** [spec 0a421c12] A launched paid campaign whose conversions cannot be
 *  read, with what the fix needs attached.
 *
 *  CARRIES THE KEY, NOT THE PROMPT, and that is a correction to the
 *  spec rather than a shortcut. The spec said the payload should hold
 *  the generated coding-agent prompt. Writing it that way meant a
 *  second generator beside buildAgentPromptFromKey — and the version I
 *  drafted was already wrong, telling founders to call `window.magig`
 *  and load a script that does not exist, where the real SDK is
 *  `window.mag` behind a two-tag stub. A prompt frozen into a row also
 *  goes stale: a card written today would still be handing out last
 *  month's install instructions a month from now.
 *
 *  So this mirrors analytics_install_nudge, which solved the same
 *  problem first: carry (key, host) and let the one generator render.
 *
 *  trackerKey is OPTIONAL because the no-tracker case is real — the
 *  brand may have none provisioned. The UI then sends the founder to
 *  setupPath instead of promising paste-and-go. */
export const campaignUnwiredConversionCardPayloadSchema = z.object({
    kind: z.literal('campaign_unwired_conversion'),
    campaignId: z.string().uuid(),
    campaignName: z.string().min(1).max(200),
    /** 'no_tracker' — nothing provisioned. 'never_fired' — provisioned,
     *  but the tag has never sent an event, so it is not installed. */
    reason: z.enum(['no_tracker', 'never_fired']),
    /** The brand's PUBLIC tracker key, safe in client code. Absent only
     *  when none could be provisioned. */
    trackerKey: z.string().min(1).optional(),
    /** First allowed host, for the prompt's placement guidance. */
    allowedHost: z.string().min(1).optional(),
    /** [spec 552600a7] The host the ad actually sends traffic to, and
     *  the one judged to have no conversion. Named in the card body
     *  because "no conversion wired" is ambiguous on a brand with
     *  several hosts — the founder needs to know WHICH page. */
    destinationHost: z.string().min(1).max(253),
    /** Where the founder wires it in the dashboard. Always /channels. */
    setupPath: z.string().min(1).max(200),
});
/** [spec cc1f7299] The platform says it sent clicks the site never saw.
 *
 *  Carries BOTH numbers because the founder's first question is "says
 *  who?" — a card asserting a shortfall without showing the two counts
 *  it compared is asking to be taken on faith about their money. */
export const campaignAttributionShortfallCardPayloadSchema = z.object({
    kind: z.literal('campaign_attribution_shortfall'),
    campaignId: z.string().uuid(),
    campaignName: z.string().min(1).max(200),
    /** What the ad platform reported for the window. */
    platformClicks: z.number().int().min(0),
    /** Distinct gclid-carrying visitors the site saw in the same window. */
    taggedArrivals: z.number().int().min(0),
    /** The window both counts were taken over. Stated so a reader can
     *  check the two halves were measured over the same days — comparing
     *  a 7d rollup against 30d of events would invent a shortfall. */
    windowDays: z.number().int().min(1).max(90),
    /** The destination, for the "a redirect is stripping it" case. */
    destinationHost: z.string().min(1).max(253),
});
/** [task 009cac0] Google has limited this ad on POLICY grounds.
 *
 *  Carries the RAW reason codes google returned alongside the phrase the
 *  founder reads, for the same reason the shortfall card carries both its
 *  counts: the first question is "says who?", and the answer is google's own
 *  field rather than a judgement of ours. It also means a reader can tell the
 *  policy limitation apart from whatever else was on the reading — the one
 *  live instance is `["CAMPAIGN_PAUSED","HAS_ADS_LIMITED_BY_POLICY"]`, and the
 *  pause is NOT why this card exists.
 *
 *  `paused` is carried explicitly rather than inferred from the codes, because
 *  it changes what the card can honestly ask for: on a paused campaign there
 *  is no spend to stop, only an appeal to file. */
export const campaignPolicyLimitedCardPayloadSchema = z.object({
    kind: z.literal('campaign_policy_limited'),
    campaignId: z.string().uuid(),
    campaignName: z.string().min(1).max(200),
    /** WHICH launched ad google limited. One campaign can carry several and
     *  google limits them independently, so the card is keyed on this rather
     *  than on the campaign alone. */
    deliverableId: z.string().min(1).max(200),
    /** Every reason code on the reading, verbatim. */
    reasons: z.array(z.string().min(1).max(100)).min(1),
    /** google's serving status on the same reading (eligible / limited /
     *  paused / …). */
    servingStatus: z.string().min(1).max(50),
    /** Whether that status means the ad is not spending. Not the same question
     *  as the policy limit, and the card says different things about each. */
    paused: z.boolean(),
    /** [task f200874] WHY it fired. 'policy' means google named a policy code.
     *  'unnameable' means google is restricting the campaign and the reason it
     *  returns exists in no api version we can ask for — it answers UNKNOWN.
     *  Carried so the card's own prose cannot drift into calling the second one
     *  a policy problem, which it has no evidence for.
     *
     *  Optional: cards written before this shipped are all 'policy'. */
    reasonKind: z.enum(['policy', 'unnameable']).optional(),
    /** When the reading that carries the limit was taken. */
    readingAt: z.string().datetime(),
    /** How long the limit has been on an unbroken run of readings, in days.
     *  Absent on the first reading that carries it — a limit noticed today is
     *  not "it has been this way for 0 days", and saying so would understate a
     *  fresh strike as much as overstate it. */
    limitedForDays: z.number().int().min(1).max(3650).optional(),
});
/** [spec 2d9fff9e] Mag has stopped raising this campaign's bids because the
 *  campaign has never recorded a conversion.
 *
 *  THE SURFACE THE OUTCOME BRAKE (spec 4ed62816) WAS MISSING. The brake
 *  refuses the raise and the refusal reached nobody, so a campaign stopped
 *  improving silently — indistinguishable from one mag had finished
 *  optimising. This says it once per campaign per climb.
 *
 *  CAMPAIGN-KEYED, unlike the policy card beside it, which is deliberately
 *  keyed on the deliverable. The brake reads conversions at CAMPAIGN scope,
 *  so every braked keyword on one campaign is one condition with one decision
 *  waiting on it — N cards would be N copies of the same sentence.
 *
 *  `measurement` IS THE HONESTY FIELD. `conversions` is notNull().default('0'),
 *  so an untracked campaign reads the same zero as a tracked failing one.
 *  'established' means a conversion has been recorded on this campaign's
 *  destination host, so the copy may say the campaign is tracked and has
 *  recorded nothing. 'unknown' means the final_url did not resolve to a host
 *  we could ask about, and the copy must make NO claim about tracking either
 *  way. Neither branch may ever assert the campaign does not convert. */
export const campaignClimbStalledCardPayloadSchema = z.object({
    kind: z.literal('campaign_climb_stalled'),
    campaignId: z.string().uuid(),
    campaignName: z.string().min(1).max(200),
    /** The dearest braked keyword — the one whose climb got furthest — as the
     *  concrete example the prose names. */
    keyword: z.string().min(1).max(200),
    criterionId: z.string().min(1).max(100),
    /** The bid mag FIRST moved this keyword to, which is what the climb is
     *  measured against. Never the bid currently in force: re-reading the
     *  current bid as the baseline is the defect the brake's own FM-4 names. */
    baselineBidUsd: z.number().nonnegative(),
    /** The bid ACTUALLY IN FORCE on that keyword — where mag's last raise left
     *  it. Carried because without it the card could only name the baseline and
     *  the refused figure, and prose built from those two reads as a claim that
     *  she made the raise she refused (task 4b61c73: the first card to land in
     *  production said "i took it from $1.50 to $14.50" when the bid was $3.41
     *  and $14.50 was what she declined).
     *
     *  OPTIONAL because cards written before 4b61c73 do not carry it — the same
     *  convention `reasonKind` uses on the policy card above. The copy degrades
     *  to naming no completed raise at all rather than guessing one. */
    currentBidUsd: z.number().nonnegative().optional(),
    /** What GOOGLE now wants for the first page — the raise the brake REFUSED.
     *  Never a bid mag set. The prose must attribute it to google and must not
     *  use a completed-action verb on it. */
    cpcBidUsd: z.number().nonnegative(),
    /** How many of the campaign's keywords are braked, this one included. The
     *  prose names one and counts the rest. */
    brakedCount: z.number().int().min(1).max(10000),
    /** Spend since launch AS THE ACCOUNT REPORTED IT — summed from
     *  `cost_native`, never `cost_usd`, which the schema documents as legacy and
     *  still misnamed: it holds the native amount unconverted.
     *
     *  ABSENT means NEVER MEASURED — no metrics rows, or a NULL sum — and is a
     *  different claim from a measured zero. Same convention `all_conversions`
     *  states for itself one table over: conflating the two would put "it has
     *  spent 0.00 since launch" in a founder's inbox on the strength of our own
     *  missing rollup. The card then says nothing about money at all. */
    spendNative: z.number().nonnegative().optional(),
    /** ISO 4217 for `spendNative`. ABSENT means UNKNOWN, and the card must then
     *  omit the amount entirely rather than render it with a '$' — telling a
     *  founder on a PEN account they spent "$154" is being wrong about the only
     *  number in the card. */
    currency: z.string().length(3).optional(),
    /** Whether measurement was POSITIVELY established on the destination host.
     *  See the note above — this field decides what the copy may claim. */
    measurement: z.enum(['established', 'unknown']),
});
/** [spec 512b83e2] Google counted a conversion and none of it can steer
 *  bidding.
 *
 *  Carries BOTH counts for the same reason the shortfall card above does:
 *  the founder's first question is "says who?", and the answer here is
 *  google's own split between the bidding goal and everything else, not a
 *  judgement of ours.
 *
 *  `observeOnlyNames` is OPTIONAL and its absence is load-bearing (C-4).
 *  policyForConversionName returns UNKNOWN_EVENT -> primaryForGoal TRUE for
 *  any conversion name we did not author, deliberately, because inferring
 *  "..._started means weak proxy" would have silently demoted a founder's
 *  only bid signal on the strength of a substring. So when the names are not
 *  ours the card still fires on google's numbers and simply does not name
 *  them: the payload cannot express a classification we refuse to make.
 */
export const campaignUnbiddableConversionCardPayloadSchema = z.object({
    kind: z.literal('campaign_unbiddable_conversion'),
    campaignId: z.string().uuid(),
    campaignName: z.string().min(1).max(200),
    /** google's all_conversions - every action, primary and secondary. Always
     *  >= 1 on a landed card; a zero here is the "nothing counted at all"
     *  case, which is not this card (C-1). */
    allConversions: z.number().min(0),
    /** google's `conversions` - PRIMARY actions only, the bidding goal. Zero
     *  on every landed card, carried anyway so the comparison is visible
     *  rather than asserted. */
    biddableConversions: z.number().min(0),
    /** The window both counts were taken over. Stated for the same reason the
     *  shortfall card states it, though the risk is lower here: both numbers
     *  come from the same GAQL row, so they cannot disagree about the window. */
    windowDays: z.number().int().min(1).max(90),
    /** The conversion names we RECOGNISE as observe-only. Absent when none of
     *  the brand's names are ours to classify - see the note above. */
    observeOnlyNames: z.array(z.string().min(1).max(100)).min(1).optional(),
    /** [spec 370880bd] Which of those names the founder DECLARED as the win.
     *
     *  Attribution, not a new fact. `planPrimaryForGoal`'s `barredPromotion`
     *  detects exactly this — a declared win the policy bars from bidding —
     *  and its only exit was a console.warn whose message ends "the founder
     *  has to ...", firing every 6h for days on magig. The fix is not a second
     *  card: magig's open card ALREADY listed signup_started among
     *  observeOnlyNames (verified live, theory a0c2bc8). What it could not say
     *  is which of them the founder had pointed Mag at.
     *
     *  Optional because the card predates this and fires on brands with no
     *  declaration at all, where there is no win to attribute. */
    declaredWin: z.string().min(1).max(100).optional(),
});
/** [spec 370880bd] The declared win is an intent event, and the brand's own
 *  spine lists a measurable completion after it.
 *
 *  SIBLING TO THE CARD ABOVE AND NOT A VARIANT OF IT. That one fires when the
 *  bidding goal is EMPTY — `allConversions >= 1` with `biddableConversions`
 *  zero, both pinned as invariants on its payload. This one fires while the
 *  goal is FULL and aimed one step too early, which is precisely why fiscalo
 *  (conversions=2, declared win already primary) was invisible to it. The two
 *  predicates are mutually exclusive on the data, so this card carries no
 *  suppression against its sibling — unlike that one's three-way ladder.
 *
 *  NO COUNTS ON THIS PAYLOAD. The sibling carries both conversion numbers so
 *  its comparison is "visible rather than asserted"; here the numbers are not
 *  the argument. A campaign can be healthy on every count and still be aimed
 *  at the wrong step, and quoting a conversion total next to that claim would
 *  invite the founder to read the number as the evidence. */
export const campaignPrematureWinCardPayloadSchema = z.object({
    kind: z.literal('campaign_premature_win'),
    /** BRAND-ANCHORED, UNLIKE ITS THREE SIBLINGS, and this was a correction
     *  made while building rather than a choice made up front.
     *
     *  The three cards above carry a campaignId because each is about one
     *  campaign's measurement. This one is about the brand's FUNNEL
     *  DECLARATION — `brand_funnel_definitions` is unique per brand — so the
     *  fact is identical for every campaign the brand runs. Anchoring it to a
     *  campaign would mean either N cards for one declaration, or picking one
     *  campaign arbitrarily and implying the problem is local to it.
     *
     *  It also could not have been built the other way: the reconciler that
     *  detects this loops brands, never campaigns, so there is no campaign in
     *  scope to anchor to. The card row's own brand_id is the address. */
    declaredWinIsBrandLevel: z.literal(true),
    /** The event the founder declared, from `semantics.steps[].role ===
     *  'final_conversion'`. Never inferred from the name — a substring rule
     *  would have demoted fiscalo's only bid signal on the strength of
     *  "..._started" (conversion-action-policy.ts's UNKNOWN_EVENT settles it). */
    declaredWin: z.string().min(1).max(100),
    /** The FIRST instrumented step after it, not all of them: the question put
     *  to the founder is "is X really the win when Y happens after it", and one
     *  concrete Y asks it better than a list. */
    successor: z.string().min(1).max(100),
    /** The predicate's own sentence, stored VERBATIM. Re-wording it here would
     *  make the detector and the card two spellings of one claim. */
    detail: z.string().min(1).max(600),
});
/**
 * Spec 5614cfab slice 2 — the campaign is losing auctions on AD RANK rather
 * than on budget, and mag has a rewrite of the ad to show.
 *
 * Its own kind rather than a campaign_funnel variant: that card's proposal
 * exists only when BUDGET dominates the loss, this one only when RANK does,
 * and the two refuse on opposite sides of the same comparison (control C-3).
 * Sharing a kind would let one gate decide whether the other is ever seen.
 *
 * NO HEADLINES ON THE PAYLOAD, deliberately. The rewrite is generated when
 * the founder opens it, against the ad as google reports it THEN — a founder
 * may have edited the ad since this card was written, and a card carrying
 * stale proposed copy would show them a diff against an ad that no longer
 * exists. The card carries the DIAGNOSIS; the drawer fetches the rewrite.
 */
export const campaignRewriteCardPayloadSchema = z.object({
    kind: z.literal('campaign_rewrite'),
    campaignId: z.string().uuid(),
    campaignName: z.string().min(1).max(200),
    /** The deliverable losing the auctions. Required here, unlike
     *  campaign_funnel's optional one: rewriteProposal refuses outright when
     *  several deliverables carry a share (C-6), so a card without one could
     *  not have been emitted. */
    deliverableId: z.string().min(1).max(64),
    /** Share of eligible impressions lost to rank, 0-1, at the moment mag
     *  spoke. The drawer prefers live state over this. */
    rankLostShare: z.number().min(0).max(1),
    /** Mag's sentence. Written free of the trade's vocabulary — this reaches
     *  the same founders the campaign page's recommendation block does. */
    line: z.string().min(1).max(600),
    windowDays: z.number().int().min(1).max(90),
    generatedAt: z.string().datetime(),
});
/**
 * [spec cbff564a C-7] The searches this campaign is paying for that the brand
 * does not sell — filed when mag found them and may NOT exclude them herself.
 *
 * IT CARRIES THE TERMS, not a count of them. The control says the card must
 * never be vaguer than the heads-up the autonomous path would have sent, and
 * the read that produced them is a live Google call made behind
 * cheaperLeverUntried — nothing persists search terms (spec c785b77b RES-1),
 * so a card holding only a number would be describing evidence no surface can
 * get back. The founder reads the actual words here or nowhere.
 *
 * EACH TERM CARRIES ITS OWN REASON, and the reason may not be its conversion
 * count. At a 5% rate 0.95^18 is about 0.40, so an 18-click term with no
 * signup is well within noise — the honest ground is that the search is for
 * something else, which is what the agent is asked for and what is stored.
 *
 * THE COST FIGURES ARE OURS, read off Google's own report rather than echoed
 * back by the agent, so there is nothing for it to mis-transcribe.
 */
/** How many terms one wasted-terms action or card may carry.
 *
 *  IT LIVES HERE, in the package both sides import, because it is two
 *  constraints that must agree: the api caps the list it acts on, and this
 *  schema is the compile-time contract for the payload it writes. Written
 *  twice, the two would drift silently — nothing PARSES this schema on a write
 *  path (feedCards.payload is plain jsonb typed with $type<FeedCardPayload>()),
 *  so a payload that broke the contract would be persisted rather than
 *  rejected. One constant is what keeps them honest.
 *
 *  Deliberately small: nothing in Magig removes a campaign criterion (theory
 *  cb55204), so a tick that excluded twenty searches would reshape a
 *  campaign's targeting in a way the founder cannot walk back. */
export const MAX_WASTED_TERMS_PER_ACTION = 5;
export const campaignWastedTermsCardPayloadSchema = z.object({
    kind: z.literal('campaign_wasted_terms'),
    campaignId: z.string().uuid(),
    campaignName: z.string().min(1).max(200),
    /** The deliverable whose search terms were read. Required: findWastedTerms
     *  is called per deliverable and a term list without its ad is not something
     *  the founder can go and act on. */
    deliverableId: z.string().min(1).max(64),
    /** What mag would have excluded, COSTLIEST FIRST, already capped by
     *  MAX_WASTED_TERMS_PER_ACTION above. Non-empty — a card with no terms is the
     *  'none' answer, which files nothing.
     *
     *  THE ORDER IS OURS, NOT THE AGENT'S [spec 8eb1b019 S-2]. This comment used
     *  to say "in the order she ranked them", and that was a ranking nobody ever
     *  asked for: the agent's seven prompt rules govern the reason and the match
     *  type and say nothing about sequence, so the list arrived in whatever order
     *  the model emitted and the cap kept the first five of it. findWastedTerms
     *  now attaches each term's spend and sorts by it before capping. It cannot be
     *  the agent's job — rule 2 forbids it from reasoning about the figures at
     *  all — and it has to be somebody's, or the five the founder sees are not
     *  the five costing the most. */
    terms: z
        .array(z.object({
        text: z.string().min(1).max(200),
        /** How she would have excluded it. Shown because PHRASE and EXACT are
         *  very different promises about what else stops matching. */
        matchType: z.enum(['EXACT', 'PHRASE', 'BROAD']),
        /** Why this search is not what the brand sells. Founder-facing. */
        reason: z.string().min(1).max(400),
        /** What it cost. Context, never the ground for excluding it. */
        clicks: z.number().int().min(0),
        costUsd: z.number().min(0),
    }))
        .min(1)
        .max(MAX_WASTED_TERMS_PER_ACTION),
    /** Mag's own summary of the read, so the card body and the drawer quote one
     *  sentence rather than two paraphrases of it. */
    rationale: z.string().min(1).max(600),
    /** Why she could not do it herself. THREE VALUES BECAUSE THE FOUNDER'S NEXT
     *  MOVE DIFFERS, and collapsing them tells them to go and fix the wrong
     *  thing:
     *    - 'no_authority' — autopilot is off for this brand. A switch they own.
     *    - 'blocked' — she is allowed and something stopped her this time (the
     *      spend envelope, no owner to notify, a queue refusal). Nothing for them
     *      to change; the next tick may well succeed.
     *    - 'not_wired' — we had not built the executor. Unreachable for cards
     *      written after spec cbff564a slice C, and kept because cards written
     *      before it are still open and still render.
     */
    blockedReason: z.enum(['no_authority', 'blocked', 'not_wired']),
    windowDays: z.number().int().min(1).max(90),
    generatedAt: z.string().datetime(),
});
/**
 * Campaign nudge — drops in after onboarding when the brand has no
 * campaigns yet. The card is mostly a CTA: prose body + a button into
 * /brand/$slug/campaigns/new/brief. Lives in the feed (not floating
 * UI) so it inherits the standard ack / dismiss controls — once the
 * founder briefs their first campaign, the nudge is auto-dismissed
 * server-side and a regular Mag flow takes over.
 */
export const campaignNudgeCardPayloadSchema = z.object({
    kind: z.literal('campaign_nudge'),
    // Why Mag is nudging right now. `analytics` means the card adopted the
    // "traffic, but no funnel" copy because visitors are already landing.
    // The dashboard forwards this to the brief route so Mag's opener can
    // acknowledge what brought the founder here.
    source: z.enum(['analytics']).optional(),
});
/**
 * Analytics install nudge — drops in after a tracker is auto-created
 * for the brand (post-URL-scrape). Mag carries the install snippet
 * inline so the founder doesn't need to bounce to /analytics for the
 * happy path. Auto-dismissed when the first event lands.
 *
 * Payload carries the public tracker key + allowed host. The full
 * snippet is assembled on the client because the script URL + ingest
 * origin depend on the dashboard's env (prod vs preview).
 */
export const analyticsInstallNudgeCardPayloadSchema = z.object({
    kind: z.literal('analytics_install_nudge'),
    trackerKey: z.string().min(1),
    allowedHost: z.string().min(1),
});
/** [spec be4289a9, slice 1] Mag can prove this brand controls a domain,
 *  and is asking permission to record it.
 *
 *  ONE DOMAIN PER CARD, and one open card per brand. A brand with several
 *  unverified domains gets the next one proposed after this is answered,
 *  rather than a card carrying three buttons — because confirming is a
 *  per-domain grant and a card that bundles them would be asking for three
 *  decisions behind one click.
 *
 *  `events` and `lastSeen` are the evidence itself, carried so the founder
 *  can judge it rather than take mag's word: "52 events, most recently two
 *  hours ago" is checkable, "i detected your tag" is not. */
export const domainVerificationAvailableCardPayloadSchema = z.object({
    kind: z.literal('domain_verification_available'),
    /** The host being proposed. Always one the brand advertises. */
    domain: z.string().min(1).max(253),
    /** Events observed on that host or beneath it, inside the sweep window. */
    events: z.number().int().nonnegative(),
    /** ISO timestamp of the most recent such event. */
    lastSeen: z.string().min(1),
});
/** [spec be4289a9, slice 3] The unverified-domain spend ceiling, said out
 *  loud before it bites.
 *
 *  ONE KIND, TWO STAGES, rather than two kinds. The spec described "a card
 *  when approaching and a different one when crossed", and two kinds is the
 *  literal reading — but a brand crossing from 70% to 100% would then hold
 *  two open cards about one domain, or need one dismissed to land the other.
 *  The same conversation at higher urgency updates in place instead.
 *
 *  `canVerifyNow` is carried because the card must not instruct a founder to
 *  do something the product cannot do. When it is false there is no
 *  self-serve route for that domain yet and the copy says so. */
export const domainCeilingWarningCardPayloadSchema = z.object({
    kind: z.literal('domain_ceiling_warning'),
    domain: z.string().min(1).max(253),
    stage: z.enum(['approaching', 'blocked']),
    spentCents: z.number().int().nonnegative(),
    ceilingCents: z.number().int().positive(),
    canVerifyNow: z.boolean(),
});
/** Mag's proactive read of a launched analytics_setup campaign (s131).
 *  Lands on /home from a scheduled scan (daily/weekly cadence); the
 *  founder taps it to jump to the launched-page panel where the full
 *  insight + diff chips live. Manual "ask mag what's changed" presses
 *  skip writing this card — the founder is already on the campaign
 *  page reading the result. */
export const campaignInsightCardPayloadSchema = z.object({
    kind: z.literal('campaign_insight'),
    /** Campaign the insight is about — the dashboard renders an "open
     *  campaign →" link that nav's to /brand/<slug>/campaigns/new/
     *  launched/<campaignId>. */
    campaignId: z.string().uuid(),
    /** Mag's name for the campaign at draft time (e.g. "wizard funnel
     *  visibility pass"). Saves the dashboard a round-trip to fetch the
     *  campaign just to render the card title. */
    campaignName: z.string().min(1).max(120),
    /** Insight row id. Lets the dashboard scroll the launched-page panel
     *  straight to the right entry in case there are multiple unacked
     *  cards covering the same campaign. */
    insightId: z.string().uuid(),
    /** Short one-line recommendation Mag wrote — duplicated here from
     *  the insight row so the card footer can render the actionable line
     *  inline ("↘ what i'd do · rewrite the wizard step 3 copy") without
     *  a join. */
    recommendation: z.string().min(1).max(400),
    /** Length of the founder ↔ mag chat thread on this insight (s159.12).
     *  Lets the home feed button flip from "ask mag about this" to
     *  "N messages" without round-tripping for the full thread on every
     *  card render. Optional so older serialized cards still validate;
     *  the dashboard treats `undefined` as 0. */
    messageCount: z.number().int().nonnegative().optional(),
});
/** One tool call mag made + its result (s172d). Persisted per mag
 *  turn so the chat UI can render "↘ mag checked: events_search · …"
 *  and ops can debug investigations after the fact. Mirrors the
 *  ToolCallTranscriptEntry shape the runAgentWithTools loop emits;
 *  defined here so the wire shape is one Zod truth across the
 *  agent → api → dashboard boundary. */
export const toolCallEntrySchema = z.object({
    /** The model's tool_use id — stable across the round. */
    id: z.string(),
    /** Tool name ('events_search', 'users_cohort', etc). */
    name: z.string(),
    /** Args the model passed. Free-form per tool. */
    arguments: z.record(z.string(), z.any()),
    /** Stringified result we replied with. Capped to ~2000 chars by
     *  the loop so persistence stays compact. */
    result: z.string(),
    isError: z.boolean(),
    /** Wall-clock latency of the tool execution itself. */
    durationMs: z.number().nonnegative(),
});
/** One message in a mag_chat thread (s141 → s142 → s172d). role='founder'
 *  for the human's turn, role='mag' for the agent's reply. createdAt
 *  is the wall-clock ISO timestamp the api stamps when the message
 *  was persisted; the chat drawer reads it for relative-ago labels.
 *  toolCalls is set on mag turns when she investigated via analytics
 *  tools before composing the reply. */
export const magChatMessageSchema = z.object({
    role: z.enum(['founder', 'mag']),
    body: z.string().min(1).max(4000),
    createdAt: z.string().datetime(),
    /** Analytics investigation transcript on this turn (s172d). Mag
     *  turns only; undefined on founder turns and on legacy mag turns
     *  born before s172d. */
    toolCalls: z.array(toolCallEntrySchema).optional(),
});
/** "Ask mag" chat card (s141). Single-turn today: a Q + A pair sits
 *  on the payload. s142 extends with an optional `messages` array
 *  that, when present, supersedes the Q + A pair for rendering — the
 *  card becomes the entry point into a multi-turn thread in the chat
 *  drawer. Keeping both fields lets the s141 surface keep working
 *  unchanged after s142 ships.
 *
 *  s216-54h.38 — mag can now initiate the thread too. `initiator`
 *  marks who spoke first. When 'mag', question + answer are omitted
 *  (there was no founder question) and the drawer reads from
 *  messages[] directly. Used by the welcome ping that replaces the
 *  old static welcome card. */
export const magChatCardPayloadSchema = z.object({
    kind: z.literal('mag_chat'),
    /** Who started the conversation. Defaults to 'founder' (back-compat
     *  with every s141/s142 card). 'mag' = unprompted ping, e.g. the
     *  welcome chat. */
    initiator: z.enum(['founder', 'mag']).optional(),
    /** The founder's question. Mirrored at the top level of the payload
     *  even when messages[] exists, so the home card has a clear single-
     *  line summary without scanning the thread. Omitted on mag-initiated
     *  cards (initiator='mag') since mag spoke first. */
    question: z.string().min(1).max(2000).optional(),
    /** Mag's first answer. Same rationale — keep the home card cheap
     *  to render regardless of thread depth. Omitted on mag-initiated
     *  cards; messages[0] holds the opener instead. */
    answer: z.string().min(1).max(4000).optional(),
    /** Multi-turn thread (s142). Undefined on s141-only cards. When
     *  present, the chat drawer renders these turns; the founder can
     *  append. For founder-initiated cards the first two entries mirror
     *  question + answer; for mag-initiated, messages[0] is mag's
     *  opener and the founder hasn't replied yet. */
    /** [spec e85da0ee slice 3] Raised 50 → 100. The 50 traced to f4f2dd76, the
     *  commit that built chat as a SINGLE-TURN composer, with no recorded
     *  reason — a sanity bound on an array nobody expected to grow. It now has
     *  to sit above the designed bound instead of below it: rollover fires at
     *  8,000 history tokens, which lands at 58-78 messages on measured exchange
     *  weights, with an 80-message backstop. At 50 the inherited cap would
     *  reject the write before the real bound ever triggered. 100 leaves
     *  headroom over the backstop so a thread is always closed by policy and
     *  never by a validation failure. */
    messages: z.array(magChatMessageSchema).max(100).optional(),
    /** s162.25 — haiku-generated digest of the whole conversation,
     *  refreshed after each mag turn once the thread has 2+ mag replies.
     *  Rendered on the home feed card so the founder sees an evolving
     *  summary instead of just the initial mag answer (which is the
     *  fallback when summary is null on short threads). 2–3 sentences,
     *  mag voice. Optional on the wire so legacy cards deserialize. */
    summary: z.string().max(1200).nullable().optional(),
    /** [spec e85da0ee slice 3] The thread this one rolled over FROM, when it was
     *  opened by rollover rather than by the founder. Makes a long conversation
     *  traceable across the cards it spans — without it, two cards for one
     *  campaign are indistinguishable from a duplicate-insert bug, which is
     *  exactly the thing C-6 is about. */
    rolledOverFrom: z.string().uuid().optional(),
    /** [spec e85da0ee slice 3] The CLOSED thread's digest, carried forward so
     *  the new thread starts knowing what was already said.
     *
     *  Deliberately NOT `summary`: that field is the running digest and
     *  appendMagChatTurn overwrites it once this thread has two mag replies, so
     *  storing the carried text there would lose it on the second turn. This one
     *  is immutable for the life of the card and is fed to the agent on EVERY
     *  turn of the new thread.
     *
     *  It is working memory's handoff, not a replacement for the closed thread:
     *  once the new thread has its own history the digest matters less, and the
     *  closed thread remains searchable through search_brand_memory because
     *  mag_chat cards are already projected into brand_episodes (theory
     *  a6b71a2). Same 1200 cap as `summary` — it holds the same kind of text. */
    carriedSummary: z.string().max(1200).nullable().optional(),
});
/** Open follow-up the nudger surfaces to /home (s161). One row per
 *  visible nudge — the nudger bumps the same card on each cycle by
 *  resetting createdAt + nudge_count rather than spawning new cards.
 *  Three founder actions (done / working / drop) post to
 *  /api/followups/:id/<action> and update the parent mag_followups row.
 *  Payload is a frozen snapshot so /home doesn't have to join. */
export const magFollowupCardPayloadSchema = z.object({
    kind: z.literal('mag_followup'),
    /** Parent mag_followups row id — the action endpoints take this in
     *  the URL. */
    followupId: z.string().uuid(),
    /** Campaign the followup belongs to. Null for future surfaces (home
     *  mag_chat extractions etc) that aren't pinned to one campaign. */
    campaignId: z.string().uuid().nullable(),
    /** Campaign name at extraction time. Renders in the campaign badge
     *  on the card (matches the campaign_insight card pattern). */
    campaignName: z.string().min(1).max(120).nullable(),
    /** Mag-voice one-line summary — surfaces as the card headline. */
    summary: z.string().min(1).max(280),
    /** Full original ask (the chat reply that spawned the followup).
     *  Rendered as collapsible details under the card so the founder
     *  can re-read what mag actually said without leaving /home. */
    body: z.string().min(1).max(8000),
    /** How many times the nudger has bumped this followup. Lets the
     *  card escalate tone past 1 ("still on this?", "should i drop it?"). */
    nudgeCount: z.number().int().nonnegative(),
});
/** Celebration card posted by the s162 detector when an open
 *  followup auto-closes via a matching analytics signal. Distinct
 *  kind so the home feed can render it with a "win" visual treatment
 *  vs. the nag-y "still chasing" tone of an open mag_followup. */
export const magFollowupCompleteCardPayloadSchema = z.object({
    kind: z.literal('mag_followup_complete'),
    /** The followup row that just closed. Founder doesn't need to act —
     *  this is informational — but the id lets the dashboard deep-link
     *  to the chat thread the followup came from when we add that. */
    followupId: z.string().uuid(),
    campaignId: z.string().uuid().nullable(),
    campaignName: z.string().min(1).max(120).nullable(),
    /** Same one-line summary the nudge card carried. */
    summary: z.string().min(1).max(280),
    /** Mag-voice line for the detected signal — "wizard_started fired
     *  47 times with wizard_type set in the last 24h". Pre-rendered
     *  server-side so the dashboard renders one fixed string. */
    detectedLine: z.string().min(1).max(280),
});
/** [12.2L-112] Policy-issue feed card. One per (campaign, ad, topic);
 *  dedup lives on ad_policy_issues. Carries enough for the renderer
 *  to show topic + severity + evidences + a deep link to the campaign
 *  page without a second query. */
export const policyIssueCardPayloadSchema = z.object({
    kind: z.literal('policy_issue'),
    campaignId: z.string().uuid(),
    campaignName: z.string().min(1).max(200),
    /** providerAdId from the snapshot. */
    providerAdId: z.string().min(1).max(120),
    /** Founder-facing ad name from the snapshot. */
    adName: z.string().min(1).max(200),
    /** Google's PolicyTopicEntry.topic (e.g. "DESTINATION_NOT_WORKING"). */
    topic: z.string().min(1).max(120),
    /** Severity: PROHIBITED / LIMITED / FULLY_LIMITED. */
    severity: z.string().min(1).max(32),
    /** Per-evidence strings from Google's PolicyTopicEvidence — the
     *  detector flattens textList + destinationTextList + url-type
     *  hints into a single bullets array. */
    evidences: z.array(z.string().max(500)).max(20),
    /** The ad's finalUrl when the snapshot carries one — surfaced in
     *  the card body for DESTINATION_NOT_WORKING so the founder sees
     *  the broken URL at a glance. Null on legacy snapshots. */
    finalUrl: z.string().max(2000).nullable(),
});
/** [12.2L-121] AI strategist phase 3 — feed card payload for a fresh
 *  strategy proposal. The full StrategyProposal sits on the
 *  `strategy_proposals` row; the card payload carries enough for the
 *  /home renderer to show the gist + accept/dismiss inline, without
 *  a second roundtrip. proposalId links back to the full row for the
 *  detail view. */
export const strategyProposalCardPayloadSchema = z.object({
    kind: z.literal('strategy_proposal'),
    proposalId: z.string().uuid(),
    /** Mag-voice campaign name from the proposal — e.g. "long-tail
     *  certificados coverage". */
    proposalName: z.string().min(1).max(200),
    /** Mag's hypothesis paragraph for why this campaign is worth a try. */
    hypothesis: z.string().min(1).max(1200),
    /** Sum of channels[].dailyBudgetUsd * 30, lifted from the proposal
     *  so the card can render it without re-walking the structure.
     *
     *  This is MAG'S RECOMMENDATION. The founder may choose a different number
     *  when accepting (spec b59688f0); that choice lives on the spawned campaign
     *  row and never overwrites this. */
    monthlyBudgetUsd: z.number().nonnegative(),
    /** [spec b59688f0] Monthly dollars below which this campaign can never
     *  reach a verdict — 10 conversions x the predicted CPA, the outcome
     *  scorer's own floor.
     *
     *  Computed SERVER-SIDE and shipped on the card so the accept UI can warn
     *  live as the founder types without hardcoding MIN_CONVERSIONS_FOR_VERDICT
     *  in TypeScript, where it would drift from the scorer the moment either
     *  moved. Optional because cards written before this shipped do not carry
     *  it. */
    verdictFloorUsd: z.number().nonnegative().optional(),
    /** Per-channel summary with a small keyword sample + KP citations
     *  so the founder sees concrete evidence on the card body. */
    channels: z
        .array(z.object({
        platform: adPlatformSchema,
        campaignName: z.string().min(1).max(200),
        dailyBudgetUsd: z.number().nonnegative(),
        keywordCount: z.number().int().nonnegative(),
        /** Top N keywords with their KP-grounded volume + competition
         *  midpoints. Renderer shows these as a chip row. */
        keywordSample: z
            .array(z.object({
            text: z.string().min(1).max(80),
            monthlySearchVolumeMidpoint: z.number().nonnegative(),
            competition: z.string().min(1).max(20),
        }))
            .max(8),
        /** [12.2L-165] Meta-channel sub-kind. Lets the dashboard render
         *  the promote_post tile differently from the cold_traffic one
         *  ("i want to boost this post" vs "i designed these new ads").
         *  Omitted on google channels. */
        metaKind: z.enum(['cold_traffic', 'promote_post']).optional(),
        /** Promote_post breadcrumbs the renderer shows so the founder
         *  reads the agency move without leaving the card. All optional
         *  — populated only when metaKind === 'promote_post'. */
        promotedPostPermalink: z.string().url().max(500).optional(),
        promotedPostCaptionExcerpt: z.string().max(280).optional(),
        promotedPostEngagementRatio: z.number().nonnegative().max(100).optional(),
        /** Promote_post or cold_traffic — the URL of the visual the
         *  founder sees on the tile (IG media URL for promote_post,
         *  bridge-rendered PNG for cold_traffic). Optional so legacy
         *  cards without a visual still parse. */
        creativePreviewUrl: z.string().url().max(2000).optional(),
    }))
        .min(1)
        .max(4),
    /** Frozen at card-create time so the renderer doesn't recompute. */
    expectedOutcome: z.object({
        cpaUsdEstimate: z.number().nonnegative(),
        conversionsPerMonthEstimate: z.number().nonnegative(),
        confidence: z.enum(['low', 'medium', 'high']),
    }),
    /** YYYY-MM-DD, brand-tz. Renderer shows "proposed for $date". */
    proposedStartDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** Lifecycle state the renderer reflects: accepted moves the card
     *  to acked + shows "accepted — campaigns spawned" banner; dismissed
     *  moves to dismissed + shows the reason if present. Source of truth
     *  is strategy_proposals.status; this is a frozen-at-card-create
     *  snapshot updated when the founder acts. */
    state: z.enum(['open', 'accepted', 'dismissed']).default('open'),
    /** [multi-market] ISO 3166-1 alpha-2 market this proposal was researched
     *  for. A multi-market brand gets one proposal card per market; the
     *  renderer shows the market chip. Optional: single-market / pre-multi-
     *  market cards omit it. */
    marketCountry: z.string().length(2).optional(),
});
/** [12.2L-171] Competition analyst feed card. Lands after the agent
 *  finishes a pass. State 'open' = founder hasn't reviewed; ack →
 *  the landscape persists on brain (already done by the agent at
 *  runtime; the card is the audit log). Dismiss → mag re-asks at
 *  next quarterly tick.
 *
 *  Schema deliberately carries the SUMMARY + recommendations preview
 *  inline on the card (not the full competitor[] block) — the
 *  per-competitor detail lives in brand_brain.competitive_landscape
 *  and the card detail page reads from there. Keeps card payload
 *  bounded. */
export const competitionLandscapeCardPayloadSchema = z.object({
    kind: z.literal('competition_landscape'),
    /** Mag-voice summary from the landscape pass — 3-4 sentences in
     *  longer-form languages. Cap matches the brain's competitive
     *  landscape softCap (800) so the payload schema doesn't reject
     *  what the agent legitimately produces. Prior 280 was a holdover
     *  from when the agent itself capped at 280 (s216-54h.8). */
    summary: z.string().min(1).max(800),
    /** Mag's overall confidence on the pass, 0-10. UI shows
     *  ('high'/'medium'/'low'/null) buckets, not the raw number. */
    confidence: z.number().int().min(0).max(10),
    /** Counts for the card header chips. */
    counts: z.object({
        competitors: z.number().int().min(0).max(12),
        recommendations: z.number().int().min(0).max(10),
        discovered: z.number().int().min(0).max(8),
        founderNamed: z.number().int().min(0).max(8),
    }),
    /** Top 3 recommendations the card surfaces inline. Founder taps to
     *  see the rest in the brand-brain detail page. */
    topRecommendations: z
        .array(z.object({
        headline: z.string().min(1).max(140),
        actionable: z.enum(['content', 'paid', 'positioning', 'pricing', 'brand', 'product']),
    }))
        .max(3),
    /** Brain version the landscape was committed to. Lets the
     *  dashboard render a "from brain v3" trail without re-querying. */
    brainVersion: z.number().int().min(1),
    /** Card lifecycle state — open / acked / dismissed. Mirrors the
     *  pattern other learn cards use. */
    state: z.enum(['open', 'acked', 'dismissed']).default('open'),
    /** [multi-market] ISO 3166-1 alpha-2 market this landscape covers. A
     *  multi-market brand gets one card per market; the renderer shows the
     *  market chip. Optional: pre-multi-market cards (and the primary, for
     *  back-compat) may omit it — absent means the brand's primary market. */
    marketCountry: z.string().length(2).optional(),
});
/** Campaign import reviewer summary card. Emitted once per import
 *  when the deterministic red-flag pass produced ≥1 finding. The
 *  payload carries the same findings shape that lives on
 *  TakeoverRead.findings (defined in ad-platform.ts) — we redeclare
 *  the schema locally so this module can stay independent of the
 *  takeover-read import graph. */
const importReviewFindingPayloadSchema = z.object({
    kind: z.enum([
        'policy_disapproved',
        'url_unreachable',
        'thin_ad_group',
        'low_ctr',
        'spend_no_convs',
        'missing_extensions',
    ]),
    severity: z.enum(['high', 'medium', 'low']),
    description: z.string().min(1).max(200),
    resourceId: z.string().max(120).optional(),
    resourceLabel: z.string().max(160).optional(),
});
export const importReviewCardPayloadSchema = z.object({
    kind: z.literal('import_review'),
    /** Magig campaign uuid the review is about — used by the renderer
     *  to deep-link into /campaigns/imported/<id>. */
    importedCampaignId: z.string().uuid(),
    /** Provider-side campaign name at import time, for the card title
     *  ("what i found in <campaignName>"). */
    campaignName: z.string().min(1).max(200),
    /** Full findings array as produced by the reviewer, ordered by
     *  severity desc. Renderer typically shows the top 3 inline and a
     *  "+N more" tail. */
    findings: z.array(importReviewFindingPayloadSchema).min(1).max(20),
    /** Tally of high-severity findings — drives the card's pulse-tinted
     *  accent on /home when ≥1. Always derivable from `findings` but
     *  cached on the payload so the renderer can pick the chrome
     *  without re-walking the array. */
    blockerCount: z.number().int().min(0).max(20),
});
/** Generic "i need data to do X" gate. Any agent that can't run
 *  because of missing brand-level setup (target country, timezone,
 *  conversion definition, etc.) can emit one of these. The dashboard
 *  renders the missing fields + a deep-link to the settings surface
 *  that fixes them. Idempotent per (brandId, agent, missing-set) via
 *  the emitter's open-card dedupe. */
export const setupNeededCardPayloadSchema = z.object({
    kind: z.literal('setup_needed'),
    /** Internal slug of the agent that's blocked. Lowercase + hyphen-
     *  cased — e.g. 'strategist', 'meta-engagement-digest',
     *  'cross-channel-budget'. Surfaces in the card eyebrow. */
    agent: z.string().min(1).max(60),
    /** Each missing field — what's gone, why it matters, where to fix
     *  it. The dashboard renders them as a clickable list inside the
     *  feed-card drawer. */
    missing: z
        .array(z.object({
        /** Stable id of the missing field — used by the open-card
         *  dedupe + the click-through target. Slug-case
         *  (e.g. 'target_country', 'brain_tz',
         *  'conversion_definition'). */
        field: z.string().min(1).max(80),
        /** Founder-facing label rendered as the row title. */
        label: z.string().min(1).max(80),
        /** One-line mag-voice description of WHY this is needed +
         *  what mag will do with it. */
        rationale: z.string().min(1).max(280),
        /** Internal dashboard route the drawer's "open settings →"
         *  link targets. Validates as a relative path. */
        settingsHref: z.string().min(1).max(200),
    }))
        .min(1)
        .max(8),
    /** Deterministic signature over `missing[].field` — the
     *  emitter writes it so the open-card dedupe can match exactly
     *  without re-walking the array. */
    signature: z.string().min(1).max(160),
});
/** 48/N — per-slot final-approval card. Emitted by slot-jit-prep when
 *  a slot transitions to final_pending (autopilot=false channel +
 *  T-leadTime fired + draft + media landed). The dashboard renders
 *  these in the polaroid-stack home surface; clicking opens the slot
 *  approval drawer where the founder approves, rejects, or asks for
 *  changes. Idempotent per slotId via the emitter's open-card dedupe;
 *  resolving the underlying slot via any of the three actions
 *  dismisses the card server-side. */
export const slotApprovalPendingCardPayloadSchema = z.object({
    kind: z.literal('slot_approval_pending'),
    slotId: z.string().uuid(),
    brandChannelId: z.string().uuid(),
    channelKind: z.enum(['instagram', 'linkedin', 'tiktok', 'youtube']),
    format: z.enum([
        'post',
        'carousel',
        'reel',
        'story',
        'linkedin_post',
        'linkedin_article',
        'video',
    ]),
    pillar: z.string().min(1).max(80),
    concept: z.string().min(1).max(280),
    /** First ~140 chars of the rendered caption — enough for the
     *  polaroid preview without dragging the home payload size. The
     *  drawer reads the full caption from the slot row. */
    captionPreview: z.string().max(280),
    /** Rendered media URL (image OR video). Drives the polaroid hero
     *  + the drawer's media preview. Optional only because edge cases
     *  (designer mode delivering placeholder, render-still-pending
     *  glitches) shouldn't break card emission. */
    mediaUrl: z.string().url().max(2048).optional(),
    mediaKind: z.enum(['image', 'video']),
    /** Brand-tz local "Jun 19 · 7:00 am" — pre-formatted server-side
     *  so the polaroid doesn't need brand-tz lookup. */
    scheduledForLabel: z.string().min(1).max(60),
    /** Raw ISO so the drawer can render relative time + a precise
     *  countdown ("publishes in 14h"). */
    scheduledForISO: z.string().datetime(),
});
/** 50c/N — slot slipped its scheduled date. Emitted when the slip
 *  handler reschedules an awaiting_designer slot to the next
 *  cadence window because the designer didn't deliver in time.
 *  Classified as 'decision' so the founder can chase the designer
 *  or flip the slot to mag-mode from the drawer. */
export const slotSlippedCardPayloadSchema = z.object({
    kind: z.literal('slot_slipped'),
    slotId: z.string().uuid(),
    brandChannelId: z.string().uuid(),
    channelKind: z.enum(['instagram', 'linkedin', 'tiktok', 'youtube']),
    format: z.enum([
        'post',
        'carousel',
        'reel',
        'story',
        'linkedin_post',
        'linkedin_article',
        'video',
    ]),
    concept: z.string().min(1).max(280),
    /** What it was scheduled for before the slip. */
    originalScheduledForISO: z.string().datetime(),
    originalScheduledForLabel: z.string().min(1).max(60),
    /** Where the handler moved it to. Null when no window was
     *  available + the slot was dropped from the plan instead. */
    newScheduledForISO: z.string().datetime().nullable(),
    newScheduledForLabel: z.string().min(1).max(60).nullable(),
    /** Free-text rationale rendered in the drawer. */
    reason: z.string().min(1).max(280),
    /** Designer's email so the founder can chase them directly. Null
     *  when the slip wasn't designer-related (future variant). */
    designerEmail: z.string().email().nullable(),
});
/** Channel onboarding pending — the founder connected an organic
 *  channel (instagram today) but hasn't run the intake conversation
 *  yet, so mag is just sitting on a live token without a plan. The
 *  card nudges them to "chat with mag" → opens the intake drawer on
 *  /channels/<kind>. Sections to needs_you. Idempotent emission: one
 *  open card per (brand, channelKind); dismissing the card stops the
 *  nudge but leaves the channel ready for the founder to start later
 *  from the channels page directly. */
export const channelOnboardingPendingCardPayloadSchema = z.object({
    kind: z.literal('channel_onboarding_pending'),
    /** Which channel mag is asking to run. Drives the deep-link path
     *  + the "↘ instagram · let me run this" eyebrow on the card. */
    channelKind: z.enum(['instagram', 'linkedin', 'tiktok', 'youtube']),
    /** brand_channels row id — lets the dashboard skip a lookup when
     *  it opens the drawer. Optional only because legacy rows that
     *  predate the card cap don't carry it; new emissions always set
     *  it. */
    brandChannelId: z.string().uuid().optional(),
    /** Founder-facing channel name (lowercase, no period). Renders as
     *  the card title; usually mirrors the channelKind ("instagram")
     *  but kept separate so we can localize later without touching the
     *  enum. */
    channelLabel: z.string().min(1).max(40),
});
/** [54h.125] Posted once when a brand's conversion funnel is first
 *  detected (>=2 steps with real traffic) and no funnel intake has run.
 *  Opens the funnel-intake conversation — Mag interviews the founder to
 *  learn what each step IS (payment gate, deferrable, etc.) so her later
 *  structural recommendations rest on confirmed knowledge, not guesses. */
export const funnelIntakePendingCardPayloadSchema = z.object({
    kind: z.literal('funnel_intake_pending'),
    /** The detected funnel step names (the conversion-event spine), so the
     *  card previews "wizard_started -> ... -> request_paid" without a lookup. */
    stepNames: z.array(z.string().min(1).max(120)).min(2).max(8),
});
/** Founder-clicked or autopilot-driven paid ad just shipped to the
 *  platform. Surfaces under WINS on /home so a launched campaign
 *  actually appears in the founder's day-of view instead of staying
 *  invisible until the next briefing cycle picks it up. One card per
 *  successful real launch (mock launches don't emit — they're not a
 *  shipped result). The card title, body, channel chip are derived
 *  server-side from the payload + adPlatform; the dashboard only
 *  needs the structured fields below. */
export const paidLaunchedCardPayloadSchema = z.object({
    kind: z.literal('paid_launched'),
    /** Which platform the ad shipped to. Drives the chip color +
     *  "↘ launched on google ads" / "↘ launched on meta ads" wording. */
    platform: adPlatformSchema,
    /** The campaign on Magig's side that owns this deliverable. Lets
     *  the card link straight to /brand/<slug>/campaigns/<id>. */
    campaignId: z.string().uuid(),
    /** Founder-facing campaign name (mirrors what mag shows in the
     *  campaign list — lowercase, no period). Renders as the card
     *  title. */
    campaignName: z.string().min(1).max(120),
    /** The Magig deliverable id (search-alquiler / search-prestamo /
     *  meta_paid_ad uuid). Used as the targetLabel — appears in the
     *  "↘ launched X on google" line. */
    deliverableId: z.string().min(1).max(64),
    /** Optional human label for the deliverable (ad group name,
     *  meta hook). Falls back to deliverableId when absent. */
    deliverableLabel: z.string().min(1).max(200).optional(),
    /** Daily budget the ad was launched with — surfaces in the body
     *  so the founder reads "shipped at $3/day" without opening the
     *  campaign. */
    dailyBudgetUsd: z.number().min(0).max(100_000),
    /** Provider's campaign id (google ads "customers/<n>/campaigns/<id>"
     *  tail / meta campaign id). Lets the founder cross-check in ads
     *  manager. */
    providerCampaignId: z.string().min(1).max(120).optional(),
});
/** Mag autonomously paused a launched paid ad.
 *
 *  THE ASYMMETRY THIS EXISTS TO CLOSE: paid_launched above is a card,
 *  so mag announced every ad she shipped and none that she killed. A
 *  founder found out two days late by opening the campaign page, and
 *  asked — reasonably — why the agency performed an action and said
 *  nothing about it.
 *
 *  Classified ALWAYS_RECEIPT / 'noticed' rather than 'wins': a dead ad
 *  is not a win. It becomes a decision only once it carries a
 *  successor the founder can approve, which is a later slice. */
export const paidPausedCardPayloadSchema = z.object({
    kind: z.literal('paid_paused'),
    platform: adPlatformSchema,
    campaignId: z.string().uuid(),
    campaignName: z.string().min(1).max(120),
    deliverableId: z.string().min(1).max(64),
    deliverableLabel: z.string().min(1).max(200).optional(),
    /** Mag's own words for why, computed at the decision site and
     *  carried verbatim so the card and the audit row can never
     *  disagree. Reads e.g. "ctr 0.50% over 6 days (20 clicks / 4000
     *  impressions) at $40.00 spend — below the 1.0% floor. paused."
     *
     *  Trustworthy as of spec 243ab33b and NOT BEFORE: until then this
     *  string reported an average of overlapping cumulative snapshots
     *  and a spend that counted the same dollars once per poll. Putting
     *  it on a card before that fix would have broadcast a wrong
     *  reason rather than merely filing one. */
    reason: z.string().min(1).max(400),
    /** What the window actually held, so the founder can check mag's
     *  arithmetic against the campaign rollup on the same page. */
    spendUsd: z.number().min(0).max(1_000_000),
    ctrPercent: z.number().min(0).max(100),
    /** What took its place — and the ABSENCE is the important case, so
     *  it is a required field rather than an optional one. 'none' must
     *  render as visible copy ("nothing replaced it"), never as
     *  silence.
     *
     *  'bench_queued' says QUEUED on purpose: planRotationPromotions
     *  only stamps the promotion on the draft, and the next autolaunch
     *  tick is what ships it. A card claiming a replacement is live
     *  would be describing an intention. */
    successor: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('none') }),
        z.object({
            kind: z.literal('bench_queued'),
            deliverableId: z.string().min(1).max(64),
            deliverableLabel: z.string().min(1).max(200).optional(),
        }),
    ]),
});
/** A paid ad-platform connection's OAuth grant went permanently dead —
 *  Google returned `invalid_grant` (refresh token revoked/expired), or
 *  Meta rejected the token (code 190/102). The refresh job flips the
 *  connection to `disconnected` and lands this card so the founder
 *  actually finds out instead of the dashboard silently freezing on the
 *  last good snapshot. Classified 'decision' / sectioned 'needs_you':
 *  mag can't read spend or launch until the founder reconnects, so it's
 *  a real ask. One open card per (brand, platform) — the emitter dedupes
 *  on payload.platform; a successful reconnect auto-dismisses it. */
export const connectionReauthRequiredCardPayloadSchema = z.object({
    kind: z.literal('connection_reauth_required'),
    /** Which platform needs reconnecting. Drives the chip + the
     *  "↘ reconnect google ads" / "↘ reconnect meta ads" CTA + the
     *  /connect/start deep-link path. Only google + meta have OAuth
     *  connections today (tiktok isn't wired). */
    platform: z.enum(['google', 'meta']),
    /** Founder-facing account label (e.g. "Account 295-042-7211" for
     *  google, the ad-account name for meta). Renders in the body so the
     *  founder knows which account dropped. */
    accountLabel: z.string().min(1).max(120),
    /** Provider's own account id — google customer_id / meta ad-account
     *  id. Optional; surfaces as a cross-check hint, not required to act. */
    providerCustomerId: z.string().min(1).max(120).optional(),
    /** One-line mag-voice reason the grant died ("the login expired",
     *  "you revoked access"). Optional; the body falls back to a generic
     *  line when absent. */
    reason: z.string().min(1).max(200).optional(),
});
/** Mag's paid autonomy was RESTRAINED — spec 70247db7 / decision 905508b.
 *
 *  Either she was demoted a level for crossing a spend cap the founder set,
 *  or she refused to act because no ceiling exists at all. Classified
 *  'decision' / sectioned 'needs_you' because there is something to choose:
 *  set a ceiling, or grant the level back.
 *
 *  It exists because authority used to move in silence. Drops surfaced only
 *  in the weekly memo's trust-check section, which is up to seven days after
 *  a brand stopped publishing — long enough that "my ads went quiet" reads as
 *  a product bug rather than a guardrail working. */
export const paidAutonomyRestrainedCardPayloadSchema = z.object({
    kind: z.literal('paid_autonomy_restrained'),
    /** The lane that moved, e.g. 'google_paid_publish'. Not narrowed to the
     *  DecisionType union on purpose: decision_type is a varchar column and a
     *  card written from a row naming a lane this build does not know must
     *  still render rather than fail validation. */
    decisionType: z.string().min(1).max(64),
    /** Where it was, and where it landed. Absent fromLevel means the restraint
     *  was a refusal rather than a demotion — nothing moved, she declined. */
    fromLevel: z.enum(['A0', 'A1', 'A2', 'A3']).optional(),
    toLevel: z.enum(['A0', 'A1', 'A2', 'A3']),
    /** Why, in mag's own words. Reused verbatim from the drop's receipt when
     *  one exists, so the card and the trust ledger cannot tell the founder
     *  two different stories. */
    receipt: z.string().min(1).max(400).optional(),
    /** True when the cause is that no monthly ceiling is configured, which has
     *  a different fix (set one) from a demotion (re-grant). Drives the CTA. */
    noCeiling: z.boolean().optional(),
});
/** Mag's paid autonomy was RESTORED — the other half of the same story.
 *
 *  A clean streak handed back a level the founder had previously granted.
 *  Classified 'receipt' / sectioned 'wins': nothing to decide, but the
 *  founder must still be told, because an automatic restoration nobody sees
 *  is a quieter version of the defect this pair exists to fix.
 *
 *  Recovery can never push a lane ABOVE what was granted (decision 905508b),
 *  so this card always describes a return to something the founder chose. */
export const paidAutonomyRestoredCardPayloadSchema = z.object({
    kind: z.literal('paid_autonomy_restored'),
    decisionType: z.string().min(1).max(64),
    fromLevel: z.enum(['A0', 'A1', 'A2', 'A3']),
    toLevel: z.enum(['A0', 'A1', 'A2', 'A3']),
    /** The level the founder originally granted. When toLevel still sits below
     *  it, recovery has further to go and the card can say so instead of
     *  implying she is all the way back. */
    grantedBaseline: z.enum(['A0', 'A1', 'A2', 'A3']).optional(),
    receipt: z.string().min(1).max(400).optional(),
});
/** [brand-drafts] Surfaces after onboarding when the brand brain still has
 *  draft rows Mag extracted but isn't sure about. Which sections those are
 *  is DRAFTABLE_SECTIONS' business, not this file's — it used to be listed
 *  here as "products / testimonials / team / voice" and went stale the day
 *  pricing plans shipped. Click opens the brain-drafts triage drawer. The
 *  emitter refreshes draftCount on each feed load + auto-dismisses at zero. */
export const brandDraftsPendingCardPayloadSchema = z.object({
    kind: z.literal('brand_drafts_pending'),
    /** Total unresolved draft rows across all brain sections — renders as
     *  "9 drafts to review" without a client-side recount. */
    draftCount: z.number().int().min(1),
    /** Per-section breakdown chips ("3 products", "1 plan", "2 quotes") so
     *  the card previews what's waiting without opening the drawer.
     *
     *  The cap is DERIVED. It was the literal 4 — which happened to equal
     *  the section count of the day — so pricing making five would have
     *  failed this parse at emit time and produced no card at all, for
     *  exactly the founders with the most left to confirm. */
    parts: z.array(z.string().min(1).max(40)).min(1).max(MAX_DRAFT_CARD_PARTS),
    /** What was outstanding per section when this card was written.
     *
     *  Exists so a dismissal can be honoured for exactly the rows the
     *  founder saw. Dismissing "3 drafts to review" is not consent to
     *  never hear about the next extraction, and comparing totals alone
     *  misses the case where confirming two products and extracting two
     *  plans leaves the total unchanged.
     *
     *  Optional: cards written before this field exist and fall back to
     *  comparing draftCount, which is correct for them. */
    countsByKind: z.record(z.string(), z.number().int().min(0)).optional(),
});
/** Discriminated union over every payload kind. Add new kinds here. */
/** [spec a41ace97] A launched campaign that is reaching nobody.
 *
 *  CARRIES THE VERDICT ITSELF rather than re-deriving a sentence. The card
 *  and the campaign page must not be able to disagree about the same
 *  campaign — two surfaces computing their own prose from the same metrics
 *  is how a page ends up carrying two opinions, and one of them wrong.
 *
 *  Fired on ENTERING not_serving and on leaving it. Never on re-entry, never
 *  per refresh tick: a campaign stuck for three days is one card.
 *
 *  Sectioned needs_you, unlike paid_paused which is a receipt of Mag acting.
 *  Here Mag has NOT acted and cannot — money is committed against nothing
 *  and the lever belongs to the founder. */
export const campaignNotServingCardPayloadSchema = z.object({
    kind: z.literal('campaign_not_serving'),
    campaignId: z.string().uuid(),
    campaignName: z.string().min(1).max(120),
    deliverableId: z.string().min(1).max(64),
    /** 'entered' is the alarm; 'left' is the all-clear, and it matters that
     *  the founder gets told when it recovers on its own. A system that only
     *  ever raises alarms teaches people to distrust the ones it clears. */
    transition: z.enum(['entered', 'left']),
    /** The verdict as the page computed it — sentence, readings, scope, lever.
     *  Stored rather than referenced so the card still reads correctly after
     *  the metrics move on. */
    verdict: campaignVerdictSchema,
    /** fetchedAt of the metrics reading that produced this transition — the
     *  card's dedup key.
     *
     *  A transition is detected by comparing the newest reading to the one
     *  before it, with no stored prior state. That is stable across runs by
     *  design, which also means it keeps being TRUE on every run until a third
     *  reading lands: the founder got the same "started serving" card twice,
     *  0.6s apart, because two refresh cycles both read the same newest pair.
     *  Keying on the reading makes a second emission for the same reading a
     *  no-op instead of a duplicate.
     *
     *  Optional because cards written before this shipped do not carry it. */
    readingAt: z.string().datetime().optional(),
});
export const feedCardPayloadSchema = z.discriminatedUnion('kind', [
    welcomeCardPayloadSchema,
    conceptCardPayloadSchema,
    creativeVariantsCardPayloadSchema,
    analyticsFindingCardPayloadSchema,
    funnelDiagnosisCardPayloadSchema,
    campaignHealthCardPayloadSchema,
    campaignFunnelCardPayloadSchema,
    campaignRewriteCardPayloadSchema,
    campaignWastedTermsCardPayloadSchema,
    campaignUnwiredConversionCardPayloadSchema,
    campaignAttributionShortfallCardPayloadSchema,
    campaignUnbiddableConversionCardPayloadSchema,
    campaignPolicyLimitedCardPayloadSchema,
    campaignClimbStalledCardPayloadSchema,
    campaignPrematureWinCardPayloadSchema,
    seoAuditCardPayloadSchema,
    aiVisibilityCardPayloadSchema,
    campaignNudgeCardPayloadSchema,
    analyticsInstallNudgeCardPayloadSchema,
    domainVerificationAvailableCardPayloadSchema,
    domainCeilingWarningCardPayloadSchema,
    campaignInsightCardPayloadSchema,
    magChatCardPayloadSchema,
    magFollowupCardPayloadSchema,
    magFollowupCompleteCardPayloadSchema,
    brandEvolutionCardPayloadSchema,
    brandEvolutionAutoAppliedPayloadSchema,
    trustMigrationProposalPayloadSchema,
    mondayRecapPayloadSchema,
    fridayBriefPayloadSchema,
    creativeDropPayloadSchema,
    monthlyPlanPayloadSchema,
    metaHistoryLearnCardPayloadSchema,
    metaEngagementDigestCardPayloadSchema,
    metaContentPlanCardPayloadSchema,
    autopilotWeeklyRecapPayloadSchema,
    crossChannelBudgetProposalPayloadSchema,
    paidStrategyRefreshProposalPayloadSchema,
    campaignRollupCardPayloadSchema,
    attributionPolicyProposalPayloadSchema,
    magCampaignProposalCardPayloadSchema,
    magChannelProposalCardPayloadSchema,
    policyIssueCardPayloadSchema,
    strategyProposalCardPayloadSchema,
    competitionLandscapeCardPayloadSchema,
    importReviewCardPayloadSchema,
    setupNeededCardPayloadSchema,
    slotApprovalPendingCardPayloadSchema,
    slotSlippedCardPayloadSchema,
    paidLaunchedCardPayloadSchema,
    paidPausedCardPayloadSchema,
    channelOnboardingPendingCardPayloadSchema,
    funnelIntakePendingCardPayloadSchema,
    connectionReauthRequiredCardPayloadSchema,
    paidAutonomyRestrainedCardPayloadSchema,
    paidAutonomyRestoredCardPayloadSchema,
    brandDraftsPendingCardPayloadSchema,
    campaignNotServingCardPayloadSchema,
]);
export const SECTION_FOR_KIND = {
    // needs_you — mag wants the founder to act.
    concept_proposal: 'needs_you',
    campaign_nudge: 'needs_you',
    analytics_install_nudge: 'needs_you',
    // [spec be4289a9] Only the founder can vouch for a domain; the sweep
    // gathers the evidence and is forbidden from acting on it.
    domain_verification_available: 'needs_you',
    // [spec be4289a9] Spend is accruing against a limit the founder cannot
    // see. Always an ask: only they can clear it.
    domain_ceiling_warning: 'needs_you',
    mag_followup: 'needs_you',
    brand_evolution: 'needs_you',
    trust_migration_proposal: 'needs_you',
    cross_channel_budget_proposal: 'needs_you',
    paid_strategy_refresh_proposal: 'needs_you',
    attribution_policy_proposal: 'needs_you',
    mag_campaign_proposal: 'needs_you',
    mag_channel_proposal: 'needs_you',
    policy_issue: 'needs_you',
    strategy_proposal: 'needs_you',
    import_review: 'needs_you',
    setup_needed: 'needs_you',
    slot_approval_pending: 'needs_you',
    slot_slipped: 'needs_you',
    channel_onboarding_pending: 'needs_you',
    funnel_intake_pending: 'needs_you',
    connection_reauth_required: 'needs_you',
    paid_autonomy_restrained: 'needs_you',
    brand_drafts_pending: 'needs_you',
    // wins — something shipped + landed.
    mag_followup_complete: 'wins',
    creative_variants: 'wins',
    brand_evolution_auto_applied: 'wins',
    paid_launched: 'wins',
    paid_autonomy_restored: 'wins',
    // watching — informational; mag noticed / read / planned / replied.
    //
    // paid_paused sits here rather than in 'wins' deliberately: killing
    // an ad is a receipt of an autonomous act, not something that
    // landed. It moves to needs_you when it starts carrying a successor
    // the founder can approve.
    paid_paused: 'watching',
    // NEEDS YOU, not watching. Every other serving state is Mag's business;
    // this one is money committed against nothing, and the lever (broaden the
    // keywords, or accept the silence) is the founder's to pull.
    campaign_not_serving: 'needs_you',
    welcome: 'watching',
    campaign_insight: 'watching',
    analytics_finding: 'watching',
    funnel_diagnosis: 'watching',
    seo_audit: 'watching',
    ai_visibility: 'watching',
    // NEEDS YOU, for the same reason campaign_not_serving is: the card's
    // own schema requires findings.min(1), so a campaign_health card
    // cannot exist without at least one underbid keyword on it, and the
    // fix is a money trade-off only the founder can make — raise the bid
    // for position, or let the term go. Its drawer renders a NEEDS A
    // DECISION chip and two buttons; filing that under "noticed" told
    // the founder mag was just observing.
    campaign_health: 'needs_you',
    // Same section, same reason: mag noticed this while watching the
    // numbers, the founder did not ask for it.
    // 'needs_you', not 'watching' — it names a ceiling the founder can lift,
    // which is a decision, not an observation. Its siblings campaign_health and
    // campaign_not_serving have always been here; this one was the outlier.
    campaign_funnel: 'needs_you',
    // 'needs_you' for the same reason as its sibling above: it names a move
    // the founder can make. And the move here is DESTRUCTIVE — applying a
    // rewrite replaces the live ad — so it is emphatically not an observation
    // to be filed under 'watching'.
    campaign_rewrite: 'needs_you',
    // [spec cbff564a C-7] 'needs_you' rather than 'watching', even though the
    // move it names has no button yet: the terms ARE the decision, and the
    // founder can act on them today through the search-terms panel on the
    // campaign page. Filing it under 'watching' would say mag is merely
    // observing money leaving.
    campaign_wasted_terms: 'needs_you',
    // [spec 0a421c12] The founder has to wire the event; mag cannot.
    campaign_unwired_conversion: 'needs_you',
    // [spec cc1f7299] The founder has to fix tagging; mag cannot.
    campaign_attribution_shortfall: 'needs_you',
    campaign_unbiddable_conversion: 'needs_you',
    // [task 009cac0] 'needs_you', and the strongest case of the family: the
    // only moves are an appeal and an ad edit, both of them google-side, and
    // the appeal has a clock. Filing a policy limit under 'watching' would say
    // mag is observing an account sanction.
    campaign_policy_limited: 'needs_you',
    // [spec 2d9fff9e] 'needs_you', and the test this file already applies
    // decides it: the only moves are the founder's. Mag has stopped raising
    // this campaign's bids and will not restart on her own, so filing it under
    // 'watching' would say she is observing a campaign she herself froze.
    campaign_climb_stalled: 'needs_you',
    // [spec 370880bd] Only the founder can say what their win is; mag can
    // only point out that something measurable happens after it.
    campaign_premature_win: 'needs_you',
    mag_chat: 'watching',
    monday_recap: 'watching',
    friday_brief: 'watching',
    creative_drop: 'watching',
    monthly_plan: 'watching',
    meta_history_learn: 'watching',
    meta_engagement_digest: 'watching',
    meta_content_plan: 'watching',
    autopilot_weekly_recap: 'watching',
    campaign_rollup: 'watching',
    competition_landscape: 'watching',
};
/** Kinds whose section is `needs_you` — used by the api's cadence
 *  helpers to filter open-asks down to actual asks (not informational
 *  cards like recaps or competition maps). */
export const NEEDS_YOU_KINDS = new Set(Object.entries(SECTION_FOR_KIND)
    .filter(([, section]) => section === 'needs_you')
    .map(([kind]) => kind));
//# sourceMappingURL=feed-card.js.map